/**
 * Created by jonas on 2022-06-15.
 */
import {gsap} from "gsap";
import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../../core/winpresentation/SimplePresentationRunner";
import {TheCage} from "../../../TheCage";
import {TheCageStatics} from "../../../config/TheCageStatics";
import {TheCageSymbol, TheCageSymbolStates} from "../../../reels/TheCageSymbol";
import {GSAPUtils} from "../../../utils/GSAPUtils";
import {Coordinate2d, SimpleReelModelSymbolData} from "../../../../core/reel/simplereel/SimpleReelModel";
import {StateMatrix} from "../../../utils/StateMatrix";
import {XBombSymbol} from "../../../reels/XBombSymbol";
import {TheCageGameAssets} from "../../../TheCageGameAssets";


export class WildMiningPresentation implements SimplePresentation {
    name:string = "WildMiningPresentation";

    constructor() {
    }

    shouldRun():boolean {
        return TheCage.model.gameData.allWildMiningPositions.length > 0;
    }

    start(controller:SimplePresentationRunnerController):void {
        const tl:gsap.core.Timeline = gsap.timeline({onComplete : () => controller.finish()});
        const lockedMatrix = new StateMatrix<boolean>([
            [false, false, false],
            [false, false, false, false],
            [false, false, false],
            [false, false, false, false],
            [false, false, false],
            [false, false, false, false],
            [false, false, false],
        ]);

        const wildMiningTls = [];
        for(let wildMiningData of TheCage.model.gameData.allWildMiningPositions) {
            lockedMatrix.setValueForCoordinates(StateMatrix.tuples2Coords(wildMiningData), true);

            const allSymbolDatas = TheCage.reelView.avalancheMatrixModel.getSymbolsAt(Coordinate2d.fromTuples(wildMiningData));
            const transformPositions = wildMiningData.slice(1, -1);
            const lastIndex = allSymbolDatas.length - 1;

            const wildMiningTl = gsap.timeline();

            const winPops = [];
            const removes = [];
            for(let data of allSymbolDatas) {
                const wmSym = <TheCageSymbol>data.positionable;
                if(wmSym.symbolName != "WB") {
                    winPops.push(wmSym.getStateAnimation(TheCageSymbolStates.WIN_POP).pause());
                    removes.push(wmSym.getStateAnimation(TheCageSymbolStates.REMOVE).pause());
                    data.removed = true;
                }
            }

            const moves = [];
            if (allSymbolDatas[0].name != "WB"){
                moves.push(this.createMoveAnimation(allSymbolDatas[0], allSymbolDatas[1]));
            }
            if (allSymbolDatas[lastIndex].name != "WB"){
                moves.push(this.createMoveAnimation(allSymbolDatas[lastIndex], allSymbolDatas[lastIndex-1]));
            }


            const symbolsToDestroy:TheCageSymbol[] = [];
            const transforms = [];
            for(let transPos of transformPositions) {
                if(TheCage.reelView.getVisibleSymbolDataAt(transPos[0], transPos[1]).name == "WB") {
                    continue;
                }
                const transformTl = gsap.timeline();
                const data = TheCage.reelView.transformSymbolAt(transPos[0], transPos[1], "WB");
                const newSym = (<XBombSymbol>data.newSymbol.positionable);
                newSym.alpha = 0;
                const oldSym = (<TheCageSymbol>data.oldSymbol.positionable);
                symbolsToDestroy.push(oldSym);
                transformTl.add(newSym.getWildMining());
                transformTl.add(gsap.to(newSym, {duration : 0.05, alpha : 1}), 0);
                transforms.push(transformTl);
            }


            wildMiningTl.add(GSAPUtils.createTimelineFromArray(winPops, "<"));
            wildMiningTl.add(GSAPUtils.createTimelineFromArray(moves, "<"));
            wildMiningTl.add(GSAPUtils.createTimelineFromArray(transforms, "<"));
            wildMiningTl.add(GSAPUtils.createTimelineFromArray(removes, "<"), "<");
            wildMiningTl.add(() => {
                //Destroy old Transform symbols.
                for(let symToDestroy of symbolsToDestroy) {
                    symToDestroy.parent.removeChild(symToDestroy);
                    symToDestroy.destroy();
                }
            })

            wildMiningTls.push(wildMiningTl);
        }


        tl.add(TheCage.winView.getWildMiningText());
        tl.add(GSAPUtils.createTimelineFromArray(wildMiningTls, "<"), 0);


        const removePositions = lockedMatrix.getAllCoordinatesForValue(false);
        const removeCoords = Coordinate2d.fromStateMatrixCoords(removePositions);

        const symbolsToExplode = TheCage.reelView.avalancheMatrixModel.getSymbolsAt(removeCoords);
        const symbolExplosions = [];
        for(let symToExpl of symbolsToExplode) {
            if(TheCageStatics.isExplodableSymbol(symToExpl.name)) {
                symToExpl.removed = true;
                symbolExplosions.push((<TheCageSymbol>symToExpl.positionable).getStateAnimation(TheCageSymbolStates.EXPLODE));
            }
        }
        const explodeTime = tl.totalDuration() -0.6;
        tl.add(GSAPUtils.createTimelineFromArray(symbolExplosions, "<"), explodeTime);
        tl.add(()=>{TheCage.reelView.getExplodeShake()}, explodeTime);




    }

    private createMoverSprite(data:SimpleReelModelSymbolData):PIXI.Sprite {
        const mainSprite = new PIXI.Sprite(TheCage.assetLoader.getTextures(TheCageGameAssets.getSymbolStateTexturePath(data.name))[0]);
        const screen = new PIXI.Sprite(TheCage.assetLoader.getTextures(TheCageGameAssets.getSymbolStateTexturePath(data.name))[0]);
        screen.blendMode = PIXI.BLEND_MODES.SCREEN;
        screen.anchor.set(0.5,0.5);

        mainSprite.addChild(screen);
        mainSprite.anchor.set(0.5,0.5);
        mainSprite.position.set(data.positionable.position.x, data.positionable.position.y);


        return mainSprite;

    }

    private createMoveAnimation(mover:SimpleReelModelSymbolData, target:SimpleReelModelSymbolData) {
        const tl = gsap.timeline({paused:true});
        const targetPos = target.positionable.position;


        const orgSym = (<TheCageSymbol>mover.positionable);
        const orgParent = orgSym.parent;
        //const orgIndex = orgParent.getChildIndex(orgSym);
        const moverSprite = this.createMoverSprite(mover);

        tl.add(()=>{
            orgParent.addChild(moverSprite);
            orgSym.visible = false;
        });
        tl.add(gsap.to(moverSprite, {
            duration : 0.2,
            x : targetPos.x,
            y : targetPos.y,
            ease : "power2.in"
        }));
        tl.add(()=>{
            orgParent.removeChild(moverSprite);
        });


        return tl;
    }
}